import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Checkbox,
  DatePicker,
  Select,
  Input,
  Tooltip,
  Row,
  Col,
  Card,
  Spin,
  Modal,
  message,
  Dropdown,
  Space,
  Tag,
} from "antd";
import {
  CheckCircleTwoTone,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloseCircleTwoTone,
  SearchOutlined,
  DatabaseOutlined,
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  BankOutlined,
  FolderOutlined,
  ProfileOutlined,
} from "@ant-design/icons";
import { RiAddBoxFill } from "react-icons/ri";
import { FcMoneyTransfer } from "react-icons/fc";
import { ColumnType } from "antd/es/table";
import { FaPen, FaTrashAlt, FaMoneyBillWave } from "react-icons/fa";
import { hasPermission } from "../../utils/HasPermission";
import EditExpenseModel from "./EditExpenseModel";
import AddExpenseModel from "./AddExpenseModel";
import {
  deleteExpense,
  exportExpenseExcel,
  getAllExpense,
  getAllExpenseForAmounts,
} from "../../utils/API";
import dayjs from "dayjs";
import { BiExport } from "react-icons/bi";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

const { Option } = Select;

interface ExpenseRecord {
  _id: string;
  key: string;
  name: string;
  date: string;
  amount: number;
  expense: string;
  expense_type: string;
  description: string;
  authorize: boolean;
}

const ExpensePage = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownsClosed, setDropdownsClosed] = useState(0);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [expenseModal, setExpenseModel] = useState(false);
  const [currentTask, setCurrentTask] = useState<ExpenseRecord | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [filteredMethod, setFilteredMethod] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<ExpenseRecord | null>(
    null,
  );
  const [dataSource, setDataSource] = useState<ExpenseRecord[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs | null>(null);
  const [exporting, setExporting] = useState(false);
  const [filteredExpenseType, setFilteredExpenseType] = useState("");

  const TwoLineClampWithTooltip = ({ text }: { text: string }) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = textRef.current;
      if (!el) return;

      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const maxHeight = lineHeight * 2;

      // Detect overflow
      if (el.scrollHeight > maxHeight + 1) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    }, [text]);

    const content = (
      <div
        ref={textRef}
        className="line-clamp-2 text-gray-800 cursor-pointer"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );

    return isOverflowing ? (
      <Tooltip
        title={
          <div
            className="max-h-[250px] overflow-y-auto p-2"
            dangerouslySetInnerHTML={{ __html: text }}
          />
        }
        placement="top"
        color="#CCCCCC"
        overlayInnerStyle={{
          color: "#000",
          padding: "12px",
          maxWidth: "500px",
          minWidth: "400px",
          maxHeight: "400px",
        }}
      >
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownsClosed((prev) => prev + 1);
  };

  const editExpense = (record: ExpenseRecord) => {
    setCurrentTask(record);
    setIsEditModalVisible(true);
    fetchExpense();
  };

  const fetchExpense = async () => {
    setLoading(true);
    try {
      // Fetch all expenses
      const response = await getAllExpenseForAmounts(1, true); // all: true

      if (response?.data && Array.isArray(response.data)) {
        // Sort by date column (latest first) - not by createdAt
        const sortedData = response.data
          .sort((a: any, b: any) => {
            // Try to parse the date column
            const dateA = getTimestampFromDate(a.date);
            const dateB = getTimestampFromDate(b.date);
            
            // If both dates are valid, sort by date
            if (dateA > 0 && dateB > 0) {
              return dateB - dateA;
            }
            // Fall back to createdAt if date is not available
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          })
          .map((expense: any, index: number) => ({
            ...expense,
            serialNumber: index + 1,
          }));

        setDataSource(sortedData);
        setTotalRecords(sortedData.length);
      } else {
        setDataSource([]);
        setTotalRecords(0);
        message.error(response.message || "Failed to fetch expenses");
      }
    } catch (error) {
      console.error("Error fetching expenses: ", error);
      message.error("Error fetching expenses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpense();
  }, [page]);

  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response: any = await deleteExpense(deleteId);
        fetchExpense(); // Refresh list

        if (response?.success) {
          message.success("Expense deleted successfully");
        }
      } catch (error) {
        console.error("Delete Error:", error);
        message.error("Error deleting expense. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  // Function to format amount
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get method color
  const getMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return "green";
      case "bank":
        return "blue";
      default:
        return "default";
    }
  };

  // Columns for the Ant Design Table
  const columns: ColumnType<ExpenseRecord>[] = [
    {
      title: "#",
      key: "index",
      fixed: "left" as const,
      width: 60,
      align: "center" as const,
      render: (_: any, __: any, index: number) => (
        <span style={{ fontWeight: 500, color: "#4B5563" }}>
          {(page - 1) * 10 + index + 1}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <UserOutlined style={{ color: "#6B7280", fontSize: "14px" }} />
          <span
            style={{
              fontWeight: 500,
              color: "#1F2937",
            }}
          >
            {text || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (text: number) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: "#059669",
              fontFamily: "monospace",
            }}
          >
            {formatAmount(text)}
          </span>
        </div>
      ),
      sorter: (a: ExpenseRecord, b: ExpenseRecord) => a.amount - b.amount,
    },
    {
      title: "Expense",
      dataIndex: "expense",
      key: "expense",
      width: 150,
      render: (text: string) => {
        // Normalize the text to handle case sensitivity (e.g., "Bank", "BANK", "bank")
        const value = text?.toLowerCase();

        // Determine colors based on the value
        let bgColor = "#F3F4F6"; // Default Gray
        let textColor = "#1F2937"; // Default Dark Gray

        if (value === "bank") {
          bgColor = "#EFF6FF"; // Light Blue
          textColor = "#2563EB"; // Blue
        } else if (value === "cash") {
          bgColor = "#F6FFED"; // Light green
          textColor = "#52C41A"; // green
        }

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: bgColor,
              padding: "4px 12px",
              borderRadius: "6px",
              width: "fit-content",
              border: `1px solid ${
                value === "bank"
                  ? "#BFDBFE"
                  : value === "cash"
                    ? "#BBF7D0"
                    : "transparent"
              }`, // Added a subtle border to make it pop
            }}
          >
            <span
              style={{
                fontWeight: 600,
                color: textColor,
              }}
            >
              {text || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Expense Type",
      dataIndex: "expense_type",
      key: "expense_type",
      width: 120,
      render: (text: string) => {
        const isCash = text?.toLowerCase() === "cash";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {/* {isCash ? (
                            <FaMoneyBillWave style={{ color: "#10b981", fontSize: "16px" }} />
                        ) : (
                            <BankOutlined style={{ color: "#2563eb", fontSize: "14px" }} />
                        )} */}
            <Tag color={getMethodColor(text)} style={{ fontWeight: 500 }}>
              {text || "N/A"}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "Description",
      key: "description",
      dataIndex: "description",
      width: 200,
      render: (text?: string) => {
        if (!text) return <Tag color="default">N/A</Tag>;
        return <TwoLineClampWithTooltip text={text} />;
      },
    },
    {
      title: "Status",
      dataIndex: "authorize",
      key: "authorize",
      width: 100,
      align: "center" as const,
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          {value ? (
            <Tag
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              color="success"
              style={{ padding: "2px 8px" }}
            >
              Authorized
            </Tag>
          ) : (
            <Tag
              icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}
              color="error"
              style={{ padding: "2px 8px" }}
            >
              Pending
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center" as const,
      render: (_: any, record: ExpenseRecord) => (
        <Space size="small">
          <Tooltip title="Edit">
            <button
              onClick={() => {
                closeAllDropdowns();
                editExpense(record);
              }}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                            shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                            hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                            active:translate-y-[1px] active:shadow-none 
                            text-white flex items-center justify-center 
                            transition-all duration-200 ease-in-out group"
            >
              <FaPen
                className="group-hover:scale-110 transition-transform duration-200"
                size={12}
              />
            </button>
          </Tooltip>

          {hasPermission("customers", "delete") && (
            <Tooltip title="Delete">
              <button
                onClick={() => {
                  closeAllDropdowns();
                  setDeleteId(record._id);
                }}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                              shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                              hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                              active:translate-y-1 active:shadow-none 
                              text-white flex items-center justify-center 
                              transition-all duration-200 group"
              >
                <FaTrashAlt
                  className="group-hover:scale-110 transition-transform"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  const getTimestampFromDate = (dateStr: string) => {
    if (!dateStr) return 0;
    // Handle DD-MM-YYYY format
    const parsed = dayjs(dateStr, ["DD-MM-YYYY", "D-M-YYYY", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed.valueOf() : 0;
  };

  // Get unique expense types for filter
  const expenseTypeOptions = Array.from(
    new Set(dataSource.map((item) => item.expense).filter(Boolean)),
  );

  // Filtered data by search text in Name column
  const filteredData = dataSource.filter((item) => {
    const searchLower = searchText.toString().toLowerCase();
    const amountStr = item.amount?.toString() || "";
    
    // Search in all relevant columns including amount
    const searchData =
      (item.name?.toString().toLowerCase() || "").includes(searchLower) ||
      (item.expense?.toString().toLowerCase() || "").includes(searchLower) ||
      (item.expense_type?.toString().toLowerCase() || "").includes(searchLower) ||
      (item.description?.toString().toLowerCase() || "").includes(searchLower) ||
      (item.date?.toString().toLowerCase() || "").includes(searchLower) ||
      amountStr.toLowerCase().includes(searchLower);

    const filteredMethodData = filteredMethod
      ? item.expense_type === filteredMethod
      : true;
    const filteredExpenseTypeData = filteredExpenseType
      ? item.expense === filteredExpenseType
      : true;

    const filteredMonthData = selectedMonth
      ? (() => {
          const startOfMonth = selectedMonth.startOf("month").valueOf();
          const endOfMonth = selectedMonth.endOf("month").valueOf();
          // Use the date column instead of ObjectId timestamp
          const itemDate = getTimestampFromDate(item.date);
          return itemDate >= startOfMonth && itemDate <= endOfMonth;
        })()
      : true;

    return (
      searchData &&
      filteredMethodData &&
      filteredMonthData &&
      filteredExpenseTypeData
    );
  });

  // Calculate total amount
  const totalAmount = filteredData.reduce((acc, item) => acc + item.amount, 0);

  const pageSize = 10; // records per page
  // Then paginate filtered data
  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params: any = {};

      if (selectedMonth) {
        params.month = selectedMonth.format("YYYY-MM");
      }

      if (filteredMethod) {
        params.expense_type = filteredMethod;
      }

      if (searchText) {
        params.search = searchText;
      }

      const response = await exportExpenseExcel(params);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const today = new Date();

      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();

      const formattedDate = `${day}_${month}_${year}`;

      link.download = `Expense_${formattedDate}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("Excel exported successfully");
    } catch (error) {
      message.error("Failed to export Excel");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const filterMenu = (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "12px",
        width: 240,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Filters</h4>

      {/* Method Filter */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Payment Method</div>
        <Select
          placeholder="Select Method"
          value={filteredMethod || undefined}
          allowClear
          style={{ width: "100%" }}
          onChange={(value) => {
            setFilteredMethod(value || "");
            setPage(1);
          }}
          options={[
            { value: "Bank", label: "Bank" },
            { value: "Cash", label: "Cash" },
          ]}
        />
      </div>

      {/* Expense Type Filter */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>Expense Type</div>
        <Select
          placeholder="Select Expense Type"
          value={filteredExpenseType || undefined}
          allowClear
          style={{ width: "100%" }}
          onChange={(value) => {
            setFilteredExpenseType(value || "");
            setPage(1);
          }}
        >
          {expenseTypeOptions.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      {/* Header Section - Matching Receipts page design */}
      <div className="m-2 mb-4">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Left side - Title and Total Amount */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 600,
                margin: 0,
                whiteSpace: "nowrap",
                color: "#111827",
              }}
            >
              Expenses
            </h2>

            <div
              style={{
                background: "white",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                padding: "4px 12px",
                display: "flex",
                height: "70px",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FcMoneyTransfer style={{ fontSize: "30px" }} />
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                Total Amount:
              </span>
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#059669",
                  whiteSpace: "nowrap",
                }}
              >
                {formatAmount(totalAmount)}
              </span>
            </div>
          </div>

          {/* Right side - All Controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              flex: "1 1 auto",
              justifyContent: "flex-end",
            }}
          >
            {/* Month Picker */}
            <DatePicker
              picker="month"
              placeholder="Select Month"
              // format="MMM YYYY"
              value={selectedMonth}
              onChange={(date) => {
                setSelectedMonth(date);
                setPage(1);
              }}
              allowClear
              style={{
                width: "140px",
                height: "40px",
                borderRadius: "8px",
              }}
              size="middle"
            />

            {/* Search with Filter Button */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                placeholder="Search expenses..."
                prefix={<SearchOutlined style={{ color: "#9CA3AF" }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{
                  width: "220px",
                  height: "40px",
                  //borderTopRightRadius: 0,
                  //borderBottomRightRadius: 0,
                  // borderRight: "none"
                }}
                size="middle"
              />
            </div>

            {/* Add Expense Button */}
            <Button
              type="primary"
              onClick={() => {
                closeAllDropdowns();
                setIsModelOpen(true);
              }}
              size="middle"
              style={{
                background: "#001529",
                height: "40px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "0 16px",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "8px",
              }}
            >
              <RiAddBoxFill style={{ fontSize: "18px" }} />
              Add
            </Button>

            {/* Export Button */}
            <Tooltip title="Export to Excel" color="#CCCCCC">
              <Button
                type="primary"
                onClick={handleExportExcel}
                loading={exporting}
                size="middle"
                style={{
                  background: "#001529",
                  height: "40px",
                  width: "40px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                }}
              >
                {!exporting && <BiExport size={18} />}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Expense?</p>
      </Modal>

      {/* Add Expense Modal */}
      <AddExpenseModel
        visible={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        onTaskCreated={() => {
          setIsModelOpen(false);
          fetchExpense();
        }}
      />

      {/* Table Card */}
      <Card
        className="mt-4"
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Spin spinning={loading}>
          <div
            className="scrollbar-hide custom-scroll-table"
            style={{ overflowX: "auto" }}
          >
            <Table
              scroll={{ x: "max-content" }}
              columns={columns}
              dataSource={paginatedData}
              loading={loading}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                current: page,
                onChange: (pageNumber) => setPage(pageNumber),
                showSizeChanger: false,
                total: filteredData.length,
              }}
              //size="middle"
              bordered={false}
              className="expense-table"
              rowClassName="hover:bg-gray-50"
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
              }}
            />
          </div>
        </Spin>
      </Card>

      {/* Edit Expense Modal */}
      {currentTask && (
        <EditExpenseModel
          visible={isEditModalVisible}
          id={currentTask?._id}
          onClose={() => setIsEditModalVisible(false)}
          onTaskCreated={function (): void {
            throw new Error("Function not implemented.");
          }}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchExpense();
          }}
        />
      )}
    </div>
  );
};

export const EXPENSES_TABLE_COLUMNS = [
  "date",
  "name",
  "amount",
  "expense",
  "expense_type",
  "description",
  "authorize",
];

export default ExpensePage;
